// Public entry: analyzePedigree(members, opts) → AnalysisResult.
//
// Pure, synchronous, no I/O. `opts.carrierStatusByMember` is the byMember map
// from the shipped deriveCarrierStatus (reused, not recomputed — plan §12.2);
// `opts.profileMatch` is the client-side Phenomizer Profile Match for
// cross-evidence. Both optional; the analyzer degrades gracefully without them.

import { tokenisePedigree } from './tokenise';
import {
  detectInheritancePatterns,
  bestInheritancePattern,
  inheritanceAssertionGate
} from './detectors/inheritance';
import { detectFamilialAggregation } from './detectors/familialAggregation';
import { detectSyndromes } from './detectors/syndromes';
import { detectCascadeCandidates } from './detectors/cascade';
import { detectAlerts } from './detectors/alerts';
import { detectCrossEvidence } from './detectors/crossEvidence';
import { buildIndividualSections } from './individualLens';
import { CONFIDENCE, PATTERN } from './constants';
import { RULESET_VERSION } from './rulesetVersion';

const PATTERN_SHORT = {
  [PATTERN.AD]: 'AD',
  [PATTERN.AR]: 'AR',
  [PATTERN.XLD]: 'XLD',
  [PATTERN.XLR]: 'XLR',
  [PATTERN.MT]: 'MT',
  [PATTERN.YL]: 'YL'
};
const RANK = { strong: 3, moderate: 2, weak: 1, ruled_out: 0, none: 0 };

function buildPatternStrip(patterns, syndromes, cascade) {
  const best = [...patterns]
    .filter((p) => p.confidence !== CONFIDENCE.none && !p.ruledOut)
    .sort((a, b) => RANK[b.confidence] - RANK[a.confidence])[0];
  const topSyndrome = [...syndromes].sort(
    (a, b) => RANK[b.confidence] - RANK[a.confidence]
  )[0];
  return {
    inheritanceLabel: best ? PATTERN_SHORT[best.id] || null : null,
    syndromeLabel: topSyndrome ? topSyndrome.label : null,
    cascadeCount: cascade.length
  };
}

// Rough generation count = longest parent chain depth + 1.
function estimateGenerations(ped) {
  const depthCache = new Map();
  const depth = (pid, seen = new Set()) => {
    if (depthCache.has(pid)) return depthCache.get(pid);
    if (seen.has(pid)) return 0;
    seen.add(pid);
    const ind = ped.byPid.get(pid);
    if (!ind) return 0;
    const parents = [ind.fatherId, ind.motherId].filter(
      (id) => id && ped.byPid.has(id)
    );
    const d = parents.length
      ? 1 + Math.max(...parents.map((p) => depth(p, seen)))
      : 0;
    depthCache.set(pid, d);
    return d;
  };
  let max = 0;
  for (const ind of ped.individuals) max = Math.max(max, depth(ind.pid));
  return max + 1;
}

export function analyzePedigree(members = [], opts = {}) {
  const {
    carrierStatusByMember = {},
    profileMatch = null,
    conditionCarrierEnabled = false,
    // Per-disease reference metadata, keyed by disease id. Fetched outside (the
    // analyzer does no I/O) — see useDiseaseMeta. A missing entry reads as
    // "assert no mode", so an unavailable terminology service degrades toward
    // stating observations without claiming mechanisms.
    diseaseMeta = null,
    // inheritanceCandidateGate. Off ⇒ every disease is admitted to Mendelian
    // inference exactly as before, so the flag genuinely restores prior
    // behaviour rather than half-applying the new rule.
    inheritanceGateEnabled = false
  } = opts;
  const ped = tokenisePedigree(members, {
    carrierStatusByMember,
    conditionCarrierEnabled
  });

  const metaFor = (diseaseId) => {
    if (!diseaseMeta) return null;
    return typeof diseaseMeta.get === 'function'
      ? diseaseMeta.get(diseaseId) || null
      : diseaseMeta[diseaseId] || null;
  };

  // Per-disease inheritance — the signal Phenomizer Phase C consumes.
  const inheritanceByDisease = {};
  const inheritancePatterns = [];
  const allPatternsByDisease = {};
  // Diseases where relatives share a diagnosis but no mode of inheritance may
  // be asserted. A DIFFERENT output, not a weaker pattern — see
  // detectors/familialAggregation.js.
  const familialAggregations = [];
  const aggregationByDisease = {};
  for (const disease of ped.familyDiseases) {
    const meta = metaFor(disease.id);
    const context = { meta, gateEnabled: inheritanceGateEnabled };

    const gate = inheritanceAssertionGate(meta, {
      gateEnabled: inheritanceGateEnabled
    });

    allPatternsByDisease[disease.id] = detectInheritancePatterns(
      ped,
      disease.id,
      context
    );

    if (!gate.allowed) {
      // No mode may be asserted. Report the observation and its limits instead
      // of silently dropping the disease from the panel — a clinician who can
      // see two affected relatives needs to see that the software noticed
      // them, and why it declined to call it a pattern.
      const aggregation = detectFamilialAggregation(
        ped,
        disease.id,
        gate,
        meta
      );
      if (aggregation) {
        familialAggregations.push(aggregation);
        aggregationByDisease[disease.id] = aggregation;
      }
      continue;
    }

    const best = bestInheritancePattern(ped, disease.id, context);
    if (best) {
      inheritanceByDisease[disease.id] = best;
      inheritancePatterns.push(best);
    }
  }

  const syndromes = detectSyndromes(ped);

  const cascadeCandidates = ped.familyDiseases.flatMap((d) =>
    detectCascadeCandidates(ped, d.id, inheritanceByDisease[d.id]).map((c) => ({
      ...c,
      diseaseId: d.id
    }))
  );

  const alerts = detectAlerts(ped);

  const family = {
    inheritancePatterns,
    inheritanceByDisease, // per-disease best pattern (for Phase C weighting)
    allPatternsByDisease, // full detector output per disease (audit/debug)
    // Diseases reported as familial aggregation because no mode of inheritance
    // may be asserted for them. Rendered as its own group, never mixed into
    // inheritancePatterns — the two are different claims.
    familialAggregations,
    aggregationByDisease,
    syndromes,
    cascadeCandidates,
    alerts,
    crossEvidence: [],
    patternStrip: buildPatternStrip(
      inheritancePatterns,
      syndromes,
      cascadeCandidates
    )
  };

  // Cross-evidence needs the family result + the (optional) Profile Match.
  family.crossEvidence = profileMatch
    ? detectCrossEvidence({ family, probandPid: ped.proband }, profileMatch)
    : [];

  const individuals = buildIndividualSections(ped, family);

  const generations = estimateGenerations(ped);
  return {
    meta: {
      rulesetVersion: RULESET_VERSION,
      structureHash: ped.structureHash,
      generatedAt: new Date().toISOString(),
      inputsSummary: {
        individuals: ped.individuals.filter((i) => !i.isDummy).length,
        generations,
        diseases: ped.familyDiseases.length
      }
    },
    family,
    individuals
  };
}
